import { useEffect, useRef } from 'react';
import { buildCtaUrl } from '../utils/attribution.js';
import { REVIEW_COUNT_LABEL, REVIEW_RATING } from '../data/reviews.js';

const APP_STORE = 'https://apps.shopify.com/account-editor';

const FLOATS = [
  { left: 13, delay: 0, rot: -7, src: '/images/edit-order.webp' },
  { left: 39, delay: 0.14, rot: 5, src: '/images/aov.webp' },
  { left: 63, delay: 0.07, rot: 7, src: '/images/cancel-saved.webp' },
  { left: 87, delay: 0.21, rot: -6, src: '/images/address-fixed.webp' },
];

const easeOut = (t) => 1 - Math.pow(1 - t, 3);

// Scroll-in floating cards that rise up through the hero and pass over the nav.
function HeroFloats() {
  const wrapRef = useRef(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const track = document.getElementById('heroTrack');
    if (!wrap || !track) return;
    const floats = Array.from(wrap.querySelectorAll('.hero__float'));
    let ticking = false;

    const place = () => {
      ticking = false;
      const rect = track.getBoundingClientRect();
      const scrollable = rect.height - window.innerHeight;
      let p = scrollable > 0 ? -rect.top / scrollable : 0;
      p = Math.max(0, Math.min(1, p));
      const DUR = 0.79;

      floats.forEach((f) => {
        const d = parseFloat(f.dataset.delay) || 0;
        const rot = parseFloat(f.dataset.rot) || 0;
        const cp = Math.max(0, Math.min((p - d) / DUR, 1));
        const ty = 58 - cp * 150;
        const enter = easeOut(Math.min(cp / 0.45, 1));
        const scale = 0.9 + 0.1 * enter;
        const blur = (1 - enter) * 4;
        f.style.transform = `translate(-50%, calc(-50% + ${ty.toFixed(2)}vh)) scale(${scale.toFixed(3)}) rotate(${rot}deg)`;
        f.style.filter = blur > 0.15 ? `blur(${blur.toFixed(2)}px)` : 'none';
        f.style.opacity = (cp < 0.74 ? 1 : Math.max(0, (1 - cp) / 0.26)).toFixed(3);
      });
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(place);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    place();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <div className="hero-floats" ref={wrapRef} aria-hidden="true">
      {FLOATS.map((f) => (
        <img
          key={f.src}
          className="hero__float"
          data-delay={f.delay}
          data-rot={f.rot}
          src={f.src}
          alt=""
          style={{ left: `${f.left}%` }}
        />
      ))}
    </div>
  );
}

export default function Hero() {
  return (
    <>
      <HeroFloats />
      <section className="hero hero--centered">
        <div className="hero__track" id="heroTrack">
          <div className="hero__sticky">
            <div className="hero__center">
              <span className="hero__badge">
                <i className="ph ph-lightning" aria-hidden="true" /> Post-purchase for Shopify
              </span>

              <h1 className="hero__headline">
                Post-purchase, <span className="hero__accent">solved.</span>
              </h1>

              <p className="hero__sub">
                Self-serve order edits, cancel deflection, and one-click upsells — live on your store
                in five minutes.
              </p>

              <div className="hero__ctas">
                <a href={buildCtaUrl(APP_STORE, 'hero-install')} className="btn btn--primary-lg">
                  Install free on Shopify <i className="ph ph-arrow-right" aria-hidden="true" />
                </a>
              </div>

              <div className="hero__proof">
                <span className="hero__stars">
                  <i className="ph-fill ph-star" aria-hidden="true" />
                  <i className="ph-fill ph-star" aria-hidden="true" />
                  <i className="ph-fill ph-star" aria-hidden="true" />
                  <i className="ph-fill ph-star" aria-hidden="true" />
                  <i className="ph-fill ph-star" aria-hidden="true" />
                </span>
                <span>{REVIEW_RATING} from {REVIEW_COUNT_LABEL} reviews · Trusted by 1,200+ Shopify brands</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
